import { Injectable } from '@angular/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Inset values reported by the native SafeArea plugin (in CSS px).
 */
export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Centralized safe-area management service.
 *
 * Reads device insets from `capacitor-plugin-safe-area`, sets CSS custom
 * properties on `:root`, and subscribes to runtime changes (rotation,
 * split-screen, fold). All other code MUST reference the CSS variables
 * instead of calling the plugin directly.
 *
 * CSS variables set:
 * - `--safe-area-inset-{top,right,bottom,left}` — real device insets
 * - `--ion-safe-area-{top,right,left}` — mirrors for Ionic components
 * - `--app-real-safe-area-bottom` — physical/native bottom inset before
 *   iOS suppression or keyboard collapse
 * - `--ion-safe-area-bottom` — always `0px` because the bottom safe area
 *   is handled globally by `body.is-native ion-app { bottom }` in CSS.
 *
 * This service is a singleton (`providedIn: 'root'`). Call `initialize()`
 * once from `AppComponent.ngOnInit()`.
 */
@Injectable({ providedIn: 'root' })
export class SafeAreaService {
  /** Reference to the native plugin (overridable in tests). */
  private safeAreaPlugin: typeof SafeArea = SafeArea;

  /** Handle returned by addListener, used to unsubscribe. */
  private listenerHandle: { remove: () => Promise<void> } | null = null;

  /** Keyboard event listener handles. */
  private keyboardListeners: Array<{ remove: () => Promise<void> }> = [];

  /** Window / visualViewport listeners used to keep insets fresh on Android. */
  private viewportListeners: Array<() => void> = [];

  /** Last inset payload received from the native plugin. */
  private nativeInsets: SafeAreaInsets | null = null;

  /** Native platform currently being handled. */
  private platform: string | null = null;

  /**
   * Read initial device insets and subscribe to changes.
   * Safe to call on any platform — skips gracefully on web.
   */
  async initialize(platform: string): Promise<void> {
    if (platform !== 'android' && platform !== 'ios') {
      return;
    }

    this.platform = platform;
    this.setupViewportListeners();

    try {
      const { insets } = await this.safeAreaPlugin.getSafeAreaInsets();
      this.nativeInsets = insets;
      this.applyResolvedInsets();

      const handle = await this.safeAreaPlugin.addListener(
        'safeAreaChanged',
        (data: { insets: SafeAreaInsets }) => {
          this.nativeInsets = data.insets;
          this.applyResolvedInsets();
        },
      );
      this.listenerHandle = handle;
    } catch {
      // Plugin unavailable (e.g. web fallback) — silently ignored.
      this.nativeInsets = null;
      this.applyResolvedInsets();
    }
  }

  /**
   * Apply inset values as CSS custom properties on `:root`.
   * Sets both our canonical `--safe-area-inset-*` variables AND the
   * Ionic-compatible `--ion-safe-area-*` overrides.
   */
  private applyInsets(insets: SafeAreaInsets, realBottomInset: number): void {
    const root = document.documentElement;
    if (!root) return;

    root.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
    root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
    root.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
    root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
    root.style.setProperty('--app-real-safe-area-bottom', `${realBottomInset}px`);

    // Override Ionic's variables so its built-in components honour our values.
    root.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
    root.style.setProperty('--ion-safe-area-right', `${insets.right}px`);
    root.style.setProperty('--ion-safe-area-left', `${insets.left}px`);

    // Bottom is ALWAYS 0 for Ionic — we handle it globally via
    // body.is-native ion-app { bottom: var(--safe-area-inset-bottom) }.
    root.style.setProperty('--ion-safe-area-bottom', '0px');
  }

  /**
   * Combine the native plugin values with a viewport-based fallback so newer
   * Android versions still reserve bottom space even when the plugin reports 0.
   */
  private applyResolvedInsets(): void {
    const nativeInsets = this.nativeInsets ?? { top: 0, right: 0, bottom: 0, left: 0 };
    const realBottomInset = this.resolveRealBottomInset(nativeInsets);
    this.applyInsets(this.resolveInsets(nativeInsets, realBottomInset), realBottomInset);
  }

  /**
   * Resolve the best current inset payload.
   *
   * The native plugin remains the source of truth when it reports a value.
   * If Android returns 0, we fall back to viewport geometry so the app still
   * preserves the gesture/navigation area on newer OS versions.
   */
  private resolveInsets(nativeInsets: SafeAreaInsets, realBottomInset: number): SafeAreaInsets {
    const bottomInset = this.isKeyboardOpen() || this.platform === 'ios'
      ? 0
      : realBottomInset;

    return {
      top: nativeInsets.top,
      right: nativeInsets.right,
      bottom: bottomInset,
      left: nativeInsets.left,
    };
  }

  /**
   * Resolve the physical/native bottom inset, preserving the Android fallback
   * and keeping the real value available even when the exposed inset is forced
   * to 0 on iOS.
   */
  private resolveRealBottomInset(nativeInsets: SafeAreaInsets): number {
    const viewportBottomInset = this.readViewportBottomInset();
    return nativeInsets.bottom > 0 ? nativeInsets.bottom : viewportBottomInset;
  }

  /**
   * Read a defensive bottom inset from the visual viewport / window geometry.
   */
  private readViewportBottomInset(): number {
    if (typeof window === 'undefined') {
      return 0;
    }

    const visualViewport = window.visualViewport;

    if (visualViewport) {
      const visualBottomInset = Math.max(
        0,
        Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop),
      );

      if (visualBottomInset > 0) {
        return visualBottomInset;
      }
    }

    const candidates: number[] = [];

    if (this.platform === 'android') {
      candidates.push(window.screen.height - window.innerHeight);
      candidates.push(window.outerHeight - window.innerHeight);
    }

    return Math.max(
      0,
      ...candidates
        .filter((value) => Number.isFinite(value))
        .map((value) => Math.round(value)),
    );
  }

  /**
   * Reapply the resolved insets when the viewport changes size.
   */
  private setupViewportListeners(): void {
    if (this.viewportListeners.length || typeof window === 'undefined') {
      return;
    }

    const sync = () => {
      this.applyResolvedInsets();
    };

    window.addEventListener('resize', sync, { passive: true });
    window.addEventListener('orientationchange', sync);
    this.viewportListeners.push(() => window.removeEventListener('resize', sync));
    this.viewportListeners.push(() => window.removeEventListener('orientationchange', sync));

    const visualViewport = window.visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener('resize', sync);
      visualViewport.addEventListener('scroll', sync);
      this.viewportListeners.push(() => visualViewport.removeEventListener('resize', sync));
      this.viewportListeners.push(() => visualViewport.removeEventListener('scroll', sync));
    }
  }

  /**
   * True when the software keyboard is currently open.
   */
  private isKeyboardOpen(): boolean {
    return typeof document !== 'undefined' && document.body.classList.contains('keyboard-open');
  }

  /**
   * Add the `is-native` CSS class to `<body>` when running on a device.
   * Should be called as early as possible (top of ngOnInit) so native-only
   * CSS rules are active before the first paint.
   */
  addNativeBodyClass(platform: string): void {
    if (platform === 'android' || platform === 'ios') {
      document.body.classList.add('is-native');
    }
  }

  /**
   * Listen for software keyboard show/hide and toggle `.keyboard-open`
   * on `<body>`. When the keyboard is open, the global CSS collapses
   * the bottom safe area so content can shift up.
   */
  setupKeyboardHandling(platform: string): void {
    if (platform !== 'android' && platform !== 'ios') {
      return;
    }

    const toggle = (open: boolean): void => {
      document.body.classList.toggle('keyboard-open', open);
    };

    try {
      Keyboard.addListener('keyboardWillShow', () => toggle(true))
        .then(h => this.keyboardListeners.push(h))
        .catch(() => {});
      Keyboard.addListener('keyboardDidShow', () => toggle(true))
        .then(h => this.keyboardListeners.push(h))
        .catch(() => {});
      Keyboard.addListener('keyboardWillHide', () => toggle(false))
        .then(h => this.keyboardListeners.push(h))
        .catch(() => {});
      Keyboard.addListener('keyboardDidHide', () => toggle(false))
        .then(h => this.keyboardListeners.push(h))
        .catch(() => {});
    } catch {
      // Keyboard plugin not available (web) — ignore.
    }
  }

  /**
   * Clean up all native listeners. Called from `ngOnDestroy`.
   */
  destroy(): void {
    try {
      if (this.listenerHandle && typeof this.listenerHandle.remove === 'function') {
        this.listenerHandle.remove().catch(() => {});
      }
    } catch { /* ignore */ }
    this.listenerHandle = null;

    for (const h of this.keyboardListeners) {
      try {
        if (h && typeof h.remove === 'function') {
          h.remove().catch(() => {});
        }
      } catch { /* ignore */ }
    }
    this.keyboardListeners = [];

    for (const cleanup of this.viewportListeners) {
      try {
        cleanup();
      } catch {
        // ignore
      }
    }
    this.viewportListeners = [];
    this.nativeInsets = null;
    this.platform = null;
  }
}
